import { Component, Input, OnInit } from '@angular/core';
import {
    Session,
    SessionService,
    CompanyService,
    BillingProcessService,
} from '../../../../_services';
import { FeatureFlagsService } from '../../../../shared';
import { ToasterService } from '../../../_shared/toaster/toaster.service';
import * as moment from 'moment';

@Component({
    selector: 'session-detail-description',
    templateUrl: 'description.component.html',
    styleUrls: [
        'description.component.scss',
        '../../../_shared/label/label.component.scss',
    ],
})
export class SessionDetailDescriptionComponent implements OnInit {
    @Input() session: Session;
    registeredCompanies: object[];
    registrationOptions: object[];

    billingActionRunning = false;
    billingActionSuccess = false;
    billingPlatonIsEnabled = false;
    externalSystemsActionRunning = false;
    externalSystemsActionSuccess = false;

    quillConfig = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ header: 1 }, { header: 2 }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ script: 'sub' }, { script: 'super' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ size: ['small', false, 'large', 'huge'] }],
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                [{ align: [] }],
                ['clean'],
            ],
        },
    };

    htmlEmail: string;

    constructor(
        private toasterService: ToasterService,
        private sessionService: SessionService,
        private companyService: CompanyService,
        private billingProcessService: BillingProcessService,
        private featureFlagsService: FeatureFlagsService
    ) {}

    async ngOnInit() {
        this.getRegisteredCompanies();
        this.getRegistrationOptions();
        this.featureFlagsService.flags.subscribe(
            ({ billingPlaton }) => (this.billingPlatonIsEnabled = billingPlaton)
        );
        this.initBillingReminderMessageForTrainer();

        if (!this.session.fees) {
            this.session.fees = {
                educFees: 0,
                educFeesCurrency: 'EURO',
                amountsPaid: 0,
                amountsPaidCurrency: 'EURO',
                lunches: 0,
                lunchesCurrency: 'EURO',
                roomRental: 0,
                roomRentalCurrency: 'EURO',
            };
        }
    }

    async getRegisteredCompanies() {
        const registrations = await this.sessionService
            .getTrainingSessionRegistrations(this.session.id)
            .toPromise();
        this.registeredCompanies = await Promise.all(
            registrations.map(({ companyId }) =>
                this.companyService.findById(companyId).toPromise()
            )
        );
    }

    async getRegistrationOptions() {
        this.registrationOptions = await this.sessionService
            .getRegistrationOptions(this.session.id)
            .toPromise();
    }

    canSendTimesheet() {
        return this.session.trainerId && this.session.location !== 'À définir';
    }

    displayPrice(
        type?: string,
        priceToDisplay?: number,
        currencyToDisplay?: string
    ): string {
        let currency: any;
        let price: any;
        if (type === 'total' || type === 'compl') {
            let priceCurrency =
                type === 'compl'
                    ? this.session.billing.priceCurrency
                    : this.session.priceCurrency;
            currency = this.displayCurrency(priceCurrency);
            price =
                type === 'compl'
                    ? this.session.billing.price / 100
                    : this.session.price / 100;
            return `${price.toFixed(2)} ${currency}`;
        } else {
            currency = this.displayCurrency(currencyToDisplay);
            return `${priceToDisplay.toFixed(2)} ${currency}`;
        }
    }

    displayCurrency(priceCurrency: any): string {
        switch (priceCurrency) {
            case 'EURO':
                return '€';
            case 'DOLLAR':
                return '$';
            default:
                return priceCurrency;
        }
    }

    generateZevalAccessId() {
        this.sessionService
            .generateZevalAccessId(this.session.id)
            .toPromise()
            .then((zeval) => {
                this.session.zevalAccessId = zeval.id;
            });
    }

    async startOrResumeBillingProcess() {
        this.billingActionRunning = true;
        try {
            const { billingAppUrl } = await this.billingProcessService
                .startOrResumeBillingProcess(this.session.id)
                .toPromise();
            window.location.href = billingAppUrl;
            this.billingActionSuccess = true;
        } catch (err) {
            console.error(err);
        } finally {
            this.billingActionRunning = false;
        }
    }

    async saveTrainerTimesheet() {
        this.billingActionRunning = true;
        try {
            await this.billingProcessService
                .saveTrainerTimesheet(this.session.id)
                .toPromise();
            this.billingActionSuccess = true;
            setTimeout(() => {
                this.billingActionSuccess = false;
            }, 2000);
        } catch (err) {
            console.error(err);
        } finally {
            this.billingActionRunning = false;
        }
    }

    sendBillingReminderToTrainer() {
        this.sessionService
            .sendBillingReminderEmailToTrainer(this.session.id, this.htmlEmail)
            .subscribe(
                () => {
                    this.toasterService.success('Message envoyé avec succès !');
                },
                () => {
                    this.toasterService.error(
                        "Erreur lors de l'envoi du message."
                    );
                }
            );
    }

    initBillingReminderMessageForTrainer(): void {
        const startDate = moment(this.session.startDate);
        const endDate = moment(this.session.startDate).add(
            this.session.days - 1,
            'days'
        );
        this.htmlEmail = `<p>Bonjour ${this.session.trainer},</p>
        <p><br></p>
        <p>Nous faisons suite à la formation <strong><i>${
            this.session.title
        }</i></strong> que tu as animé du ${startDate.format(
            'L'
        )} au ${endDate.format('L')}.</p>
        <p><br></p>
        <p><strong style="color: rgb(230, 0, 0);">Merci de bien vouloir envoyer les documents ci-dessous à :</strong><span style="color: rgb(230, 0, 0);"> </span>backoffice-training@zenika.com</p>
        <p><br></p>
        <p>Formations INTER :</p>
        <ul>
           <li>la/les <strong>feuille(s) d'émargement <u>signées par les stagiaires et toi</u></strong></li>
           <li>l'<strong>attestation de présence <u>signée par toi</u></strong></li>
           <li>les évaluations si elles n'ont pas été faites en ligne</li>
        </ul>
        <p><br></p>
        <p>Pour les formation INTRA chez le client :</p>
        <ul>
           <li><strong>Une copie de tes justificatifs de frais</strong> (nous n'avons pas accès à Alibeez, merci de nous les envoyer par scan. Les originaux sont pour la compta)</li>
           <li>la/les <strong>feuille(s) d'émargement <u>signées par les stagiaires et toi</u></strong></li>
           <li>l'<strong>attestation de présence <u>signée par toi</u></strong></li>
           <li>les évaluations si elles n'ont pas été faites en ligne</li>
        </ul>
        <p><br></p>
        <p>Pour les formation INTRA dans nos agences :</p>
        <ul>
           <li>la/les <strong>feuille(s) d'émargement <u>signées par les stagiaires et toi</u></strong></li>
           <li>l'<strong>attestation de présence <u>signée par toi</u></strong></li>
           <li>les évaluations si elles n'ont pas été faites en ligne</li>
        </ul>
        <p><br></p>
        <p>Merci de ta collaboration et bonne journée.</p>`;
    }

    async syncGoogleCalendar() {
        this.externalSystemsActionRunning = true;
        try {
            await this.sessionService
                .forceGoogleCalendarSync(this.session.id)
                .toPromise();
            this.externalSystemsActionSuccess = true;
            setTimeout(() => {
                this.externalSystemsActionSuccess = false;
            }, 2000);
        } catch (err) {
            console.error(err);
        } finally {
            this.externalSystemsActionRunning = false;
        }
    }

    async syncSignature() {
        this.externalSystemsActionRunning = true;
        try {
            await this.sessionService
                .syncSignature(this.session.id)
                .toPromise();
            this.externalSystemsActionSuccess = true;
            setTimeout(() => {
                this.externalSystemsActionSuccess = false;
            }, 2000);
        } catch (err) {
            console.error(err);
        } finally {
            this.externalSystemsActionRunning = false;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/session/detail/description/description.component.ts